import { ErrorBoundary } from "ErrorBoundary"
import { useEffect, useRef, useState } from "react"
import { Button, Card, Spinner } from "react-bootstrap"
import { useSelector } from "react-redux"
import { RootState } from "store"

import { usePrevetGetSensitivityDataForSegment } from "../hooks/usePrevetGetSensitivityDataForSegment"
import { usePrevetGetVessels } from "../hooks/usePrevetGetVessels"

import { VesselSegment } from "user/userService"
import { useFetchVesselDetails } from "../hooks/useFetchVesselDetails"
import {
  DDCategories,
  VesselStay,
  useFetchVesselStays,
} from "../hooks/useFetchVesselStays"
import { usePrevetGetRightShipData } from "../hooks/usePrevetGetRightShipData"
import { PrevetLastFiveCargoes } from "./PrevetLast5Cargoes"
import { PrevetPortStays } from "./PrevetPortStays"
import { PrevetRightShip } from "./PrevetRightShip"
import { getAvgSpeedFromPortStays } from "./PrevetPerformanceExposureCard"
import { PrevetVesselHistory } from "./PrevetVesselHistory/PrevetVesselHistory"
import { ChosenVesselForSelect, PrevetVesselSearch } from "./PrevetVesselSearch"
import { PrevetVesselSpeedWrapper } from "./PrevetVesselSpeedWrapper"
import { PrevetVesselTradingModal } from "./PrevetVesselTradingModal"

import { PrevetSpeedGraphZoneType } from "../getSpeedChartOptionsHelpers"
import { usePrevetTabStateContext } from "../hooks/prevetContext"
import {
  DEFAULT_SPEED_DATE_RANGE,
  PrevetState,
} from "../hooks/prevetStateReducer"
import { PrevetDDWidget } from "./PrevetDDWidget"
import { PrevetPerformanceExposureCard } from "./PrevetPerformanceExposureCard"
import PrevetPortAlertWidget from "./PrevetPortAlertWidget"
import PrevetProlongedStayWidget from "./PrevetProlongedStayWidget"
import PrevetSearchWidget from "./PrevetSearchWidget"
import PrevetVesselInfoCard from "./PrevetVesselInfoCard"
export type ImoNumber = string

export interface VesselListSingle {
  label: string
  value: ImoNumber
}

export interface VesselSegmentInputSelection {
  label: string
  value: VesselSegment
}

export interface SelectOption {
  label: string
  value: string
}

function ErrorFallback({ error }: { error: Error }) {
  return (
    <div>
      <p>Sorry, we are having technical issues, please contact support.</p>
      <pre>{error.message}</pre>
    </div>
  )
}

export function PrevetMain({ selectedIndex }: { selectedIndex: number }) {
  const { getTabState, getTabStateValue, setTabStateValue } =
    usePrevetTabStateContext()

  const previousSelectedIndex = useRef({ selectedIndex })

  const defaultSeaDays = 25
  const standardVesselDefaultSpeed = 12
  const [describedLadenSpeed, setDescribedLadenSpeed] = useState(
    String(standardVesselDefaultSpeed)
  )
  const [describedBallastSpeed, setDescribedBallastSpeed] = useState(
    String(standardVesselDefaultSpeed)
  )
  const [ladenSeaDays, setLadenSeaDays] = useState(String(defaultSeaDays))
  const [ballastSeaDays, setBallastSeaDays] = useState(String(defaultSeaDays))
  const [ladenFuelCon, setLadenFuelCon] = useState("")
  const [ballastFuelCon, setBallastFuelCon] = useState("")

  const user = useSelector((state: RootState) => state.user)
  const vesselSegments = user.chosenOrganization?.segments

  const [prevetSpeedGraphZoneType, setPrevetSpeedGraphZoneType] = useState<
    PrevetSpeedGraphZoneType | undefined
  >(PrevetSpeedGraphZoneType.Outliers)
  const [vesselSpeedDateRange, setVesselSpeedDateRange] = useState<string>(
    DEFAULT_SPEED_DATE_RANGE
  )
  const [vesselState, setVesselState] = useState<{
    includeBallast: boolean
    includeLaden: boolean
  }>({
    includeBallast: true,
    includeLaden: true,
  })
  const defaultSegment = user.defaultSegment

  const [chosenSegment, setChosenSegment] =
    useState<VesselSegmentInputSelection | null>(
      defaultSegment ? { value: defaultSegment, label: defaultSegment } : null
    )

  useEffect(() => {
    if (chosenSegment != null) {
      setLadenFuelCon(String(getFuelConBySegment(chosenSegment)))
      setBallastFuelCon(String(getFuelConBySegment(chosenSegment)))
    }
  }, [chosenSegment])

  const { vesselsData, getVessels, getVesselsLoading } = usePrevetGetVessels({
    vesselSegments,
  })

  const {
    vesselDetailsData,
    clearVesselDetailsData,
    fetchVesselDetails,
    getVesselDetailsLoading,
    getVesselDetailsError,
  } = useFetchVesselDetails()

  /**
   * Port Stays Data
   */
  const { vesselStaysData, isVesselStaysLoading, isVesselStaysError } =
    useFetchVesselStays({
      imo: vesselDetailsData?.imo,
      built: vesselDetailsData?.built,
    })
  const portStaysLadenAvgSpeed = getAvgSpeedFromPortStays({
    vesselStaysData,
    isLaden: true,
  })

  const portStaysBallastAvgSpeed = getAvgSpeedFromPortStays({
    vesselStaysData,
    isLaden: false,
  })

  /**
   * Speed Data
   * VesselLegs data is fetched from PrevetVesselSpeedChart
   */
  const [ladenAvgSpeed, setLadenAvgSpeed] = useState<number | undefined>()
  const [ballastAvgSpeed, setBallastAvgSpeed] = useState<number | undefined>()
  const [ladenMaxSpeed, setLadenMaxSpeed] = useState<number | undefined>()
  const [ballastMaxSpeed, setBallastMaxSpeed] = useState<number | undefined>()
  const [userLadenVesselSpeed, setUserLadenVesselSpeed] = useState<
    string | undefined
  >()
  const [userBallastVesselSpeed, setUserBallastVesselSpeed] = useState<
    string | undefined
  >()

  /**
   * Sensitivity
   */
  const { getSegmentSensitivityData, segmentSensitivityData } =
    usePrevetGetSensitivityDataForSegment()

  const ladenChartInstance = useRef<any>()
  const ballastChartInstance = useRef<any>()

  /**
   * Trading Map
   */
  const [isTradingModalShown, setIsTradingModalShown] = useState(false)
  const [dateForCenterMarker, setDateForCenterMarker] = useState<
    string | undefined
  >()

  /* Chosen vessel */
  const [chosenVessel, setChosenVessel] = useState<SelectOption | undefined>()
  const [filteredVesselStaysData, setFilteredVesselStaysData] = useState<
    VesselStay[] | undefined
  >()

  //setchosensegment
  useEffect(() => {
    if (chosenVessel) {
      const tempSegment = chosenVessel?.label.split("-").at(-1)?.replace(/[^a-zA-Z]/g, "").toLowerCase()
      if (tempSegment) {
        setChosenSegment({
          value: tempSegment as VesselSegment,
          label: tempSegment as VesselSegment,
        })
        getSegmentSensitivityData(tempSegment)
      }
    }
  }, [chosenVessel])

  //setfuelprice and hireprice
  useEffect(()=>{
    if (segmentSensitivityData) {
      setHirePrice(String(segmentSensitivityData.benchmark.spot_price))
      setFuelPrice(String(segmentSensitivityData.benchmark.fuel_price))
    }
  },[segmentSensitivityData])

  /* Date integration */

  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const today = new Date()
    return today.toISOString().split("T")[0]
  })

  /* Checking */
  useEffect(() => {
    if (vesselStaysData && selectedDate) {
      const filteredData = vesselStaysData.filter(
        (stay) => new Date(stay.date_to) <= new Date(selectedDate)
      )
      setFilteredVesselStaysData(filteredData)
    }
  }, [vesselStaysData, selectedDate])
  useEffect(()=>{
    console.log(segmentSensitivityData)
  },[segmentSensitivityData])
  const defaultHirePrice = segmentSensitivityData?.benchmark.spot_price || ""
  const defaultFuelPrice = segmentSensitivityData?.benchmark.fuel_price || ""
  const [hirePrice, setHirePrice] = useState(String(defaultHirePrice))
  const [fuelPrice, setFuelPrice] = useState(String(defaultFuelPrice))

  const [filterText, setFilterText] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const clearData = () => {
    clearVesselDetailsData()
    setBallastAvgSpeed(undefined)
    setLadenAvgSpeed(undefined)
    setBallastMaxSpeed(undefined)
    setLadenMaxSpeed(undefined)
    setFilteredVesselStaysData(undefined)
  }



  useEffect(() => {
    if (
      selectedIndex != previousSelectedIndex.current.selectedIndex ||
      getTabStateValue("imo", undefined) === undefined
    ) {
      clearData()
      setTabStateValue("perfExposureValue", "", undefined)
      const tabState = getTabState(selectedIndex) ?? ({} as PrevetState)
      if (Object.keys(tabState).length > 0) {
        const currentImo = tabState["imo"]
        const currentDate = tabState["date"]
        const currentDescribedLadenSpeed = tabState["cvdLadenSpeed"]
        const currentLadenFuelCon = tabState["cvdLadenFuelCon"]
        const currentLadenSeaDays = tabState["cvdLadenSeaDays"]
        const currentDescribedBallastSpeed = tabState["cvdBallastSpeed"]
        const currentBallastFuelCon = tabState["cvdBallastFuelCon"]
        const currentBallastSeaDays = tabState["cvdBallastSeaDays"]
        const currentVesselSpeedType = tabState["vesselSpeedGraphZoneType"]
        const currentVesselSpeedDateRange = tabState["vesselSpeedDateRange"]
        const currentPerfExposureHireRate = tabState["perfExposureHireRate"]
        const currentPerfExposureFuelPrice = tabState["perfExposureFuelPrice"]
        const currentHvIncludeBallast = tabState["hvIncludeBallast"]
        const currentHvIncludeLaden = tabState["hvIncludeLaden"]
        const currentHvSearchText = tabState["hvSearchText"]
        const currentHvCurrentPage = tabState["hvCurrentPage"]
        const currentHvRowsPerPage = tabState["hvRowsPerPage"]

        fetchVesselDetails(currentImo ? +currentImo.value : 0)
        setChosenVessel(currentImo ? currentImo : undefined)

        if (currentDescribedBallastSpeed)
          setDescribedBallastSpeed(currentDescribedBallastSpeed)
        if (currentBallastFuelCon) setBallastFuelCon(currentBallastFuelCon)
        if (currentBallastSeaDays) setBallastSeaDays(currentBallastSeaDays)

        if (currentDescribedLadenSpeed)
          setDescribedLadenSpeed(currentDescribedLadenSpeed)
        if (currentLadenFuelCon) setLadenFuelCon(currentLadenFuelCon)
        if (currentLadenSeaDays) setLadenSeaDays(currentLadenSeaDays)

        setSelectedDate(currentDate)

        setPrevetSpeedGraphZoneType(currentVesselSpeedType ?? undefined)
        setVesselSpeedDateRange(currentVesselSpeedDateRange)

        setHirePrice(currentPerfExposureHireRate)
        setFuelPrice(currentPerfExposureFuelPrice)

        setVesselState({
          includeBallast: currentHvIncludeBallast,
          includeLaden: currentHvIncludeLaden,
        })
        setFilterText(currentHvSearchText)
        setCurrentPage(currentHvCurrentPage)
        setRowsPerPage(currentHvRowsPerPage)

        const isNew = getTabStateValue("imo", undefined) === undefined
        if (isNew) {
          setDescribedLadenSpeed(standardVesselDefaultSpeed + "")
          setLadenSeaDays(defaultSeaDays + "")
          setDescribedBallastSpeed(standardVesselDefaultSpeed + "")
          setBallastSeaDays(defaultSeaDays + "")
          if (chosenSegment) {
            setLadenFuelCon(String(getFuelConBySegment(chosenSegment)))
            setBallastFuelCon(String(getFuelConBySegment(chosenSegment)))
          }
        }
      }
    }

    previousSelectedIndex.current = { selectedIndex }
  }, [selectedIndex, getTabStateValue("imo", selectedIndex)])

  /**
   * Right Ship
   */
  const { rightShipData, rightShipDataLoading } = usePrevetGetRightShipData({
    imo: Number(vesselDetailsData?.imo),
    date: selectedDate,
  })
  const rightShipVesselInfo = rightShipData?.items[0]

  const usePsCollapsed = () => {
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false)

    useEffect(() => {
      // Function to check if .ps-collapsed exists in the document
      const checkCollapsed = () => {
        setIsCollapsed(!!document.querySelector(".ps-collapsed"))
      }

      // Observe mutations in the document
      const observer = new MutationObserver(() => checkCollapsed())

      observer.observe(document.body, {
        attributes: true,
        subtree: true,
        childList: true,
      })

      // Initial check
      checkCollapsed()

      return () => {
        observer.disconnect() // Clean up observer on unmount
      }
    }, [])

    return isCollapsed
  }

  const isCollapsed = usePsCollapsed()

  // Track window width to detect “mobile”
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const mainContentRef = useRef<string | null>(null)

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  useEffect(() => {
    const mainContentEl = document.querySelector(".main-content") as HTMLElement
    if (!mainContentEl) return

    // Store the original width if not already stored
    if (!mainContentRef.current) {
      mainContentRef.current = mainContentEl.style.width
    }

    const isMobile = windowWidth < 768
    if (isMobile) {
      // Force 100vw if mobile
      mainContentEl.style.width = "100vw"
    } else {
      // If desktop, decide width based on collapsed or not
      mainContentEl.style.width = isCollapsed
        ? "calc(100vw - 90px)"
        : "calc(100vw - 260px)"
    }

    // Cleanup function to restore the original width
    return () => {
      if (mainContentRef.current) {
        mainContentEl.style.width = mainContentRef.current
      }
    }
  }, [windowWidth, isCollapsed])

  return (
    <div className="PrevetMainContainer" key={selectedIndex}>
      {filteredVesselStaysData &&
        vesselDetailsData &&
        isTradingModalShown &&
        chosenSegment && (
          <PrevetVesselTradingModal
            chosenSegment={chosenSegment}
            closeTradingModal={() => {
              setIsTradingModalShown(false)
              setDateForCenterMarker(undefined)
            }}
            isTradingModalShown={isTradingModalShown}
            vesselDetails={vesselDetailsData}
            vesselStays={filteredVesselStaysData}
            dateForCenterMarker={dateForCenterMarker}
          />
        )}
      {!vesselDetailsData && !getVesselDetailsError && (
        <Card className="PrevetMain_SearchBar">
          <Card.Body>
            <div className="VesselSearch_Row">
              <h5 className="mb-0 me-3">Search Vessel</h5>
              {defaultSegment && chosenSegment && vesselSegments && (
                <PrevetVesselSearch
                  vesselsData={vesselsData}
                  getVesselsLoading={getVesselsLoading}
                  getVesselDetailsLoading={getVesselDetailsLoading}
                  chosenSegment={chosenSegment}
                  getChosenVessel={({ imo }: ChosenVesselForSelect) => {
                    fetchVesselDetails(imo)
                    setUserLadenVesselSpeed(undefined)
                    setUserBallastVesselSpeed(undefined)
                  }}
                  setChosenSegment={(segment: VesselSegmentInputSelection) => {
                    setChosenSegment(segment)
                  }}
                  getVessels={getVessels}
                  vesselSegments={vesselSegments.map((segment) => {
                    return { label: segment, value: segment }
                  })}
                  clearChosenVesselData={() => {
                    clearData()
                  }}
                  chosenVessel={chosenVessel}
                  setChosenVessel={setChosenVessel}
                />
              )}
              <div className="VesselSearch_ButtonContainer">
                <Button
                  color="primary"
                  className="btn btn-primary w-100 waves-effect waves-light"
                  disabled={!vesselDetailsData || !filteredVesselStaysData}
                  onClick={() => {
                    setIsTradingModalShown(true)
                  }}
                >
                  {getVesselDetailsLoading || getVesselsLoading ? (
                    <Spinner
                      size="sm"
                      variant="light"
                      animation="border"
                      role="status"
                    />
                  ) : (
                    <div>
                      <span className="me-2">
                        <i className="ti-eye" />
                      </span>
                      Info & Trading
                    </div>
                  )}
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      <ErrorBoundary FallbackComponent={ErrorFallback}>
        {!vesselDetailsData && !getVesselDetailsError && (
          <div role="alert" className="fade bg-ifp mb-0 alert alert-info show">
            Please select a vessel
          </div>
        )}
        {getVesselDetailsError && (
          <div role="alert" className="fade bg-ifp mb-0 alert alert-info show">
            Unable to get vessel, please contact support.
          </div>
        )}
        {vesselDetailsData && chosenSegment && vesselSegments && (
          <>
            <div className="PrevetContainer">
              {/* Selected vessel card */}
              <PrevetSearchWidget
                ladenFuelCon={ladenFuelCon}
                ladenSeaDays={ladenSeaDays}
                describedLadenSpeed={describedLadenSpeed}
                describedBallastSpeed={describedBallastSpeed}
                ballastFuelCon={ballastFuelCon}
                ballastSeaDays={ballastSeaDays}
                setLadenFuelCon={setLadenFuelCon}
                setLadenSeaDays={setLadenSeaDays}
                setDescribedLadenSpeed={setDescribedLadenSpeed}
                setDescribedBallastSpeed={setDescribedBallastSpeed}
                setBallastFuelCon={setBallastFuelCon}
                setBallastSeaDays={setBallastSeaDays}
                ladenChartInstance={ladenChartInstance}
                ballastChartInstance={ballastChartInstance}
                speedGraphLadenAvgSpeed={ladenAvgSpeed}
                speedGraphBallastAvgSpeed={ballastAvgSpeed}
                portStaysLadenAvgSpeed={portStaysLadenAvgSpeed}
                portStaysBallastAvgSpeed={portStaysBallastAvgSpeed}
                realLadenMaxSpeed={ladenMaxSpeed}
                realBallastMaxSpeed={ballastMaxSpeed}
                segmentSensitivityData={segmentSensitivityData}
                vesselName={vesselDetailsData.vessel_name}
                defaultFuelCon={getFuelConBySegment(chosenSegment)}
                setUserLadenVesselSpeed={(speed: string | undefined) => {
                  setUserLadenVesselSpeed(speed)
                }}
                setUserBallastVesselSpeed={(speed: string | undefined) => {
                  setUserBallastVesselSpeed(speed)
                }}
                vesselsData={vesselsData}
                getVesselsLoading={getVesselsLoading}
                getVesselDetailsLoading={getVesselDetailsLoading}
                chosenSegment={chosenSegment}
                getChosenVessel={({ imo }: ChosenVesselForSelect) => {
                  fetchVesselDetails(imo)
                  setUserLadenVesselSpeed(undefined)
                  setUserBallastVesselSpeed(undefined)
                }}
                setChosenSegment={(segment: VesselSegmentInputSelection) => {
                  setChosenSegment(segment)
                }}
                getVessels={getVessels}
                vesselSegments={vesselSegments.map((segment) => {
                  return { label: segment, value: segment }
                })}
                clearChosenVesselData={() => {
                  clearData()
                }}
                getSegmentSensitivityData={(segment: string) => {
                  getSegmentSensitivityData(segment)
                }}
                chosenVessel={chosenVessel}
                setChosenVessel={setChosenVessel}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                showTradingModal={() => setIsTradingModalShown(true)}
              />
              {/* Prolonged Stay Card */}
              {isVesselStaysLoading ? (
                <Card className="PrevetContainer_ProlongedStayWidget">
                  <div className="d-flex flex-column h-100 w-100 align-items-center justify-content-center">
                    <div className="spinner-border mb-2" role="status">
                      <span className="sr-only"></span>
                    </div>
                    Loading Prolonged Stay Widget...
                  </div>
                </Card>
              ) : (
                <PrevetProlongedStayWidget
                  vesselStaysData={filteredVesselStaysData}
                  isVesselStaysLoading={isVesselStaysLoading}
                  isVesselStaysError={isVesselStaysError}
                />
              )}
              {/* Dry Dock Card */}
              <Card className="PrevetContainer_DDWidget">
                {isVesselStaysLoading && (
                  <div className="d-flex flex-column h-100 w-100 align-items-center justify-content-center">
                    <div className="spinner-border mb-2" role="status">
                      <span className="sr-only"></span>
                    </div>
                    Loading Dry Dock - IWS - Repairs Widget...
                  </div>
                )}

                {filteredVesselStaysData && !isVesselStaysLoading ? (
                  <PrevetDDWidget
                    vesselDetails={vesselDetailsData}
                    vesselStays={filteredVesselStaysData}
                  />
                ) : (
                  isVesselStaysError && (
                    <div className="d-flex flex-column h-100 w-100 align-items-center justify-content-center">
                      Error loading vessel stays
                    </div>
                  )
                )}
              </Card>
              {/* Collapsible div */}
              {/* Last 5 Cargoes card */}
              <Card className="PrevetContainer_FiveCargoes">
                {isVesselStaysLoading && (
                  <div className="d-flex flex-column h-100 w-100 align-items-center justify-content-center">
                    <div className="spinner-border mb-2" role="status">
                      <span className="sr-only"></span>
                    </div>
                    Loading Last 5 Cargoes...
                  </div>
                )}
                {filteredVesselStaysData && !isVesselStaysLoading ? (
                  <Card.Body>
                    <PrevetLastFiveCargoes
                      vesselStaysData={filteredVesselStaysData}
                    />
                  </Card.Body>
                ) : (
                  isVesselStaysError && (
                    <div className="d-flex w-100 h-100 align-items-center justify-content-center">
                      Error loading vessel stays
                    </div>
                  )
                )}
              </Card>
              {/* Port Alerts Card */}
              <PrevetPortAlertWidget vesselStays={filteredVesselStaysData} />
              {/* Rightship Card */}
              {rightShipDataLoading && (
                <Card className="PrevetContainer_RightShip d-flex align-items-center justify-content-center">
                  <Spinner animation="border" role="status" />
                  <div className="mt-4">Loading RightShip Data...</div>
                </Card>
              )}
              {rightShipVesselInfo && (
                <PrevetRightShip
                  rightShipVesselInfo={rightShipVesselInfo}
                  dateSelected={selectedDate}
                />
              )}
              {/* Vessel Speed Chart */}
              <Card className="PrevetContainer_SpeedGraph h-100">
                <PrevetVesselSpeedWrapper
                  vesselName={vesselDetailsData.vessel_name}
                  vesselImo={vesselDetailsData.imo}
                  setIsTradingModalShown={setIsTradingModalShown}
                  setDateForCenterMarker={setDateForCenterMarker}
                  setLadenAvgSpeed={setLadenAvgSpeed}
                  setBallastAvgSpeed={setBallastAvgSpeed}
                  setLadenMaxSpeed={setLadenMaxSpeed}
                  setBallastMaxSpeed={setBallastMaxSpeed}
                  ladenChartInstance={ladenChartInstance}
                  ballastChartInstance={ballastChartInstance}
                  userLadenVesselSpeed={userLadenVesselSpeed}
                  userBallastVesselSpeed={userBallastVesselSpeed}
                  endDate={selectedDate}
                  setPrevetSpeedGraphZoneType={setPrevetSpeedGraphZoneType}
                  prevetSpeedGraphZoneType={prevetSpeedGraphZoneType}
                  setVesselSpeedDateRange={setVesselSpeedDateRange}
                  vesselSpeedDateRange={vesselSpeedDateRange}
                />
              </Card>
              {/* Port stays */}
              <PrevetPortStays
                vesselDetailsData={vesselDetailsData}
                vesselStaysData={filteredVesselStaysData}
                isVesselStaysLoading={isVesselStaysLoading}
                isVesselStaysError={isVesselStaysError}
              />
              {/* Performance Exposure card */}
              <PrevetPerformanceExposureCard
                ladenFuelCon={ladenFuelCon}
                ballastFuelCon={ballastFuelCon}
                describedBallastSpeed={describedBallastSpeed}
                describedLadenSpeed={describedLadenSpeed}
                ladenSeaDays={ladenSeaDays}
                ballastSeaDays={ballastSeaDays}
                ladenChartInstance={ladenChartInstance}
                ballastChartInstance={ballastChartInstance}
                speedGraphLadenAvgSpeed={ladenAvgSpeed}
                speedGraphBallastAvgSpeed={ballastAvgSpeed}
                portStaysLadenAvgSpeed={portStaysLadenAvgSpeed}
                portStaysBallastAvgSpeed={portStaysBallastAvgSpeed}
                realLadenMaxSpeed={ladenMaxSpeed}
                realBallastMaxSpeed={ballastMaxSpeed}
                segmentSensitivityData={segmentSensitivityData}
                vesselName={vesselDetailsData.vessel_name}
                defaultFuelCon={getFuelConBySegment(chosenSegment)}
                setUserLadenVesselSpeed={(speed: string | undefined) => {
                  setUserLadenVesselSpeed(speed)
                }}
                setUserBallastVesselSpeed={(speed: string | undefined) => {
                  setUserBallastVesselSpeed(speed)
                }}
                setHirePrice={setHirePrice}
                hirePrice={hirePrice}
                setFuelPrice={setFuelPrice}
                fuelPrice={fuelPrice}
              />
              {/* Vessel Info Card */}
              <Card className="PrevetContainer_VesselInfo w-100 h-100">
                {getVesselDetailsLoading || rightShipDataLoading ? (
                  <div className="d-flex flex-column w-100 h-100 justify-content-center align-items-center text-muted">
                    <Spinner
                      className="mb-3"
                      animation="border"
                      role="status"
                    />
                    Loading vessel details...
                  </div>
                ) : (
                  vesselDetailsData &&
                  rightShipVesselInfo && (
                    <PrevetVesselInfoCard
                      vesselDetails={vesselDetailsData}
                      dateOfBuild={(rightShipVesselInfo as any).dateOfBuild}
                    />
                  )
                )}
              </Card>
            </div>
            <div className="PrevetChosenVesselData_Row mt-3">
              <Card>
                <Card.Body>
                  {isVesselStaysLoading && (
                    <div className="d-flex flex-column align-items-center">
                      <Spinner
                        className="mb-3"
                        animation="border"
                        role="status"
                      />
                      <div>Loading historical vessel data...</div>
                    </div>
                  )}
                  {isVesselStaysError && (
                    <div className="d-flex justify-content-center">
                      Error loading historical vessel data.
                    </div>
                  )}
                  {filteredVesselStaysData && (
                    <PrevetVesselHistory
                      vesselStays={filteredVesselStaysData}
                      setVesselState={setVesselState}
                      vesselState={vesselState}
                      filterText={filterText}
                      setFilterText={setFilterText}
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                      rowsPerPage={rowsPerPage}
                      setRowsPerPage={setRowsPerPage}
                    />
                  )}
                </Card.Body>
              </Card>
            </div>
          </>
        )}
      </ErrorBoundary>
    </div>
  )
}

function getFuelConBySegment(segment: VesselSegmentInputSelection) {
  if (segment.value === VesselSegment.Handysize) {
    return 18
  }
  if (segment.value === VesselSegment.Supramax) {
    return 23
  }
  if (segment.value === VesselSegment.Panamax) {
    return 25
  }
  // Otherwise chosen segment is capesize
  return 43
}
